import React, { Component } from 'react';
import { connect } from 'react-redux';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import Dialog from 'material-ui/Dialog';
import Checkbox from 'material-ui/Checkbox';
import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import DownloadIcon from 'material-ui/svg-icons/file/file-download';
import { showNotification as showNotificationAction } from 'admin-on-rest';
import { stringify } from 'query-string';
import { API_BASE_URL } from '../endpoints';

const STORAGE_KEY = 'cloudhints_click_export_prefs';

// Keep keys/labels in sync with EXPORT_COLUMNS in v1/admin/siteclick_controller.rb
const COLUMN_DEFS = [
    { key: 'id',                   label: 'ID' },
    { key: 'clickdatetime',        label: 'Datum/tijd klik' },
    { key: 'domain',               label: 'Cloudhints site' },
    { key: 'website_url',          label: 'Website URL' },
    { key: 'link_key',             label: 'Link key' },
    { key: 'link_url',             label: 'Link URL' },
    { key: 'link_description',     label: 'Link omschrijving' },
    { key: 'advertiser_user_id',   label: 'Klant ID' },
    { key: 'company_name',         label: 'Bedrijfsnaam klant' },
    { key: 'email',                label: 'E-mail klant' },
    { key: 'user_ip',              label: 'IP-adres bezoeker' },
    { key: 'user_agent',           label: 'User agent' },
    { key: 'from_site',            label: 'Van site' },
    { key: 'gcr',                  label: 'GCR' },
    { key: 'outgoinglink_raw',     label: 'Outgoing link (ruw)' },
    { key: 'clickcost',            label: 'Kosten per klik' },
    { key: 'suspectedcrawler',     label: 'Verdachte crawler' },
    { key: 'blacklistedip',        label: 'IP geblacklist' },
    { key: 'blacklist_startswith', label: 'IP-range geblacklist' },
    { key: 'blacklist_ai',         label: 'Bot-gedrag' },
    { key: 'filtereddoubles',      label: 'Dubbele klik' },
    { key: 'telt_mee',             label: 'Telt mee' },
    { key: 'created_at',           label: 'Aangemaakt op' },
];

const defaultColumns = () => {
    const cols = {};
    COLUMN_DEFS.forEach((c) => { cols[c.key] = true; });
    return cols;
};

const loadPrefs = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { format: 'csv', columns: defaultColumns() };
        const parsed = JSON.parse(raw);
        return {
            format: parsed.format === 'xlsx' ? 'xlsx' : 'csv',
            columns: { ...defaultColumns(), ...(parsed.columns || {}) },
        };
    } catch (e) {
        return { format: 'csv', columns: defaultColumns() };
    }
};

class ExportClicksButton extends Component {
    state = { dialogOpen: false, loading: false, ...loadPrefs() };

    savePrefs = (format, columns) => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ format, columns })); } catch (e) {}
    };

    openDialog = () => {
        const { filterValues = {}, showNotification } = this.props;
        if (!filterValues.from_date || !filterValues.to_date) {
            showNotification('Kies eerst een "Van datum" en "Tot en met datum" hierboven om te exporteren', 'warning');
            return;
        }
        this.setState({ dialogOpen: true });
    };

    closeDialog = () => this.setState({ dialogOpen: false });

    toggleColumn = (key) => (event, checked) => {
        const columns = { ...this.state.columns, [key]: checked };
        this.setState({ columns });
        this.savePrefs(this.state.format, columns);
    };

    setAllColumns = (checked) => () => {
        const columns = {};
        COLUMN_DEFS.forEach((c) => { columns[c.key] = checked; });
        this.setState({ columns });
        this.savePrefs(this.state.format, columns);
    };

    setFormat = (event, value) => {
        this.setState({ format: value });
        this.savePrefs(value, this.state.columns);
    };

    handleExport = () => {
        const { filterValues = {}, showNotification } = this.props;
        const { from_date, to_date, cloudhintsdomains_id, user_id } = filterValues;
        const { format, columns } = this.state;

        const selectedKeys = COLUMN_DEFS.filter((c) => columns[c.key]).map((c) => c.key);
        if (selectedKeys.length === 0) {
            showNotification('Selecteer minstens één kolom om te exporteren', 'warning');
            return;
        }

        const token = localStorage.getItem('token');
        const query = stringify({
            from_date, to_date, cloudhintsdomains_id, user_id,
            format,
            columns: selectedKeys.join(','),
        });

        this.setState({ loading: true });

        fetch(`${API_BASE_URL}/admin/siteclick_export?${query}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                if (response.ok) return response.blob();

                // Try to read the API's own error message; fall back to a message per status code.
                return response.json().catch(() => null).then((body) => {
                    let message = body && body.message;
                    if (!message) {
                        if (response.status === 401 || response.status === 403) {
                            message = 'Je bent niet (meer) ingelogd, of hebt geen rechten. Log opnieuw in en probeer het nogmaals.';
                        } else if (response.status === 404) {
                            message = 'Export-functie is niet gevonden op de server (mogelijk nog niet gedeployed).';
                        } else if (response.status >= 500) {
                            message = `De server kon de export niet maken (fout ${response.status}). Probeer het later opnieuw.`;
                        } else {
                            message = `Export mislukt (fout ${response.status}).`;
                        }
                    }
                    throw new Error(message);
                });
            })
            .then((blob) => {
                const ext = format === 'xlsx' ? 'xlsx' : 'csv';
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `siteclicks_${String(from_date).slice(0, 10)}_${String(to_date).slice(0, 10)}.${ext}`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                this.setState({ loading: false, dialogOpen: false });
            })
            .catch((e) => {
                console.error(e);
                // A fetch() call that never got a response (blocked by CORS, server unreachable, offline)
                // rejects with a TypeError instead of reaching the response handling above.
                const message = e instanceof TypeError
                    ? 'Kan de server niet bereiken. Controleer of de API draait en of je verbinding werkt.'
                    : (e.message || 'Export mislukt, probeer het opnieuw');
                showNotification(message, 'warning');
                this.setState({ loading: false });
            });
    };

    render() {
        const { dialogOpen, loading, format, columns } = this.state;

        const actions = [
            <FlatButton label="Annuleer" onClick={this.closeDialog} disabled={loading} />,
            <RaisedButton
                label={loading ? 'Bezig...' : 'Exporteren'}
                primary
                style={{ marginLeft: 8 }}
                onClick={this.handleExport}
                disabled={loading}
            />,
        ];

        return (
            <span>
                <FlatButton label="Exporteren" icon={<DownloadIcon />} onClick={this.openDialog} />
                <Dialog
                    title="Kliks exporteren"
                    modal={false}
                    open={dialogOpen}
                    onRequestClose={this.closeDialog}
                    actions={actions}
                    autoScrollBodyContent
                >
                    <p>Exporteert alle kliks (ook uitgefilterde) in de gekozen periode.</p>

                    <strong>Bestandsformaat</strong>
                    <RadioButtonGroup name="export_format" valueSelected={format} onChange={this.setFormat} style={{ marginBottom: 16 }}>
                        <RadioButton value="csv" label="CSV (komma-gescheiden)" style={{ marginTop: 8 }} />
                        <RadioButton value="xlsx" label="Excel (.xlsx)" style={{ marginTop: 8 }} />
                    </RadioButtonGroup>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <strong>Kolommen</strong>
                        <span>
                            <FlatButton label="Alles aan" onClick={this.setAllColumns(true)} />
                            <FlatButton label="Alles uit" onClick={this.setAllColumns(false)} />
                        </span>
                    </div>
                    <div style={{ columnCount: 2 }}>
                        {COLUMN_DEFS.map((c) => (
                            <Checkbox
                                key={c.key}
                                label={c.label}
                                checked={!!columns[c.key]}
                                onCheck={this.toggleColumn(c.key)}
                                style={{ marginTop: 6 }}
                            />
                        ))}
                    </div>
                </Dialog>
            </span>
        );
    }
}

export default connect(null, { showNotification: showNotificationAction })(ExportClicksButton);



// WEBPACK FOOTER //
// ./src/siteclicks/ExportClicksButton.js